import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatDistanceToNowStrict } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
    Box,
    Card,
    Typography,
    Button,
    List,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Divider,
    IconButton,
    Tooltip,
    TablePagination,
    Chip,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ShoppingCart,
    LocalShipping,
    CheckCircle,
    Info,
    Lock as LockIcon,
} from '@mui/icons-material';
import notificationService from '../../services/notificationService';
import { hasPermission, getNotificationTargetPath } from '../../utils/permissions';
import { useDataTable } from '../../hooks/useDataTable';
import { useStore } from '../../context/StoreContext';

const NOTIFICATION_ICONS = {
    ORDER_CREATED: <ShoppingCart color="secondary" />,
    ORDER_STATUS_CHANGED: <ShoppingCart color="primary" />,
    ORDER_PAYMENT_STATUS_CHANGED: <CheckCircle color="success" />,
    PURCHASE_ORDER_STATUS_CHANGED: <LocalShipping color="info" />,
};
const iconFor = (type) => NOTIFICATION_ICONS[type] || <Info color="action" />;

const NotificationsList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useSelector((state) => state.auth);
    const { currentStore } = useStore();

    // Page and size live in the URL (/notifications?page=0&size=20), newest first (server-ordered).
    const table = useDataTable(notificationService.getNotificationsPage, {}, {
        onError: () => toast.error(t('common.loadError')),
        deps: [currentStore],
    });
    const notifications = table.data.content;
    const loading = table.loading;

    // Optimistic local update of the current page (the server is updated right after).
    const patchNotifications = (fn) =>
        table.setData((prev) => ({ ...prev, content: prev.content.map(fn) }));

    const unreadCount = notifications.filter((n) => !n.read).length;

    const handleMarkAllRead = async () => {
        // "Mark all read" covers every page server-side; reflect it on the visible one immediately.
        patchNotifications((n) => ({ ...n, read: true }));
        try {
            await notificationService.markAllAsRead();
        } catch (error) {
            table.refresh();
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read) {
            patchNotifications((n) => (n.id === notification.id ? { ...n, read: true } : n));
            notificationService.markAsRead(notification.id).catch(() => {});
        }

        const targetPath = getNotificationTargetPath(notification);
        if (!targetPath) return;

        if (!hasPermission(user, notification.requiredPermission)) {
            toast.warning(t('header.noPermissionForNotification'));
            return;
        }

        navigate(targetPath);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ flexGrow: 1 }}>
                    {t('header.notifications')}
                </Typography>
                {unreadCount > 0 && (
                    <Button onClick={handleMarkAllRead} sx={{ textTransform: 'none' }}>
                        {t('header.markAllRead')}
                    </Button>
                )}
            </Box>

            <Card>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography color="text.secondary">{t('common.loading')}</Typography>
                    </Box>
                ) : notifications.length === 0 ? (
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography color="text.secondary">{t('header.noNotifications')}</Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {notifications.map((n, idx) => {
                            const targetPath = getNotificationTargetPath(n);
                            const allowed = !targetPath || hasPermission(user, n.requiredPermission);
                            return (
                                <Box key={n.id}>
                                    <ListItemButton
                                        onClick={() => handleNotificationClick(n)}
                                        sx={{ py: 1.75, backgroundColor: n.read ? 'transparent' : 'action.hover' }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: 'transparent' }}>{iconFor(n.type)}</Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1" fontWeight={n.read ? 400 : 600}>
                                                    {n.referenceType === 'PLATFORM' && (
                                                        <Chip
                                                            label="CronixLabs"
                                                            size="small"
                                                            color="primary"
                                                            sx={{ fontSize: '0.6rem', height: 16, mr: 1 }}
                                                        />
                                                    )}
                                                    {n.title}
                                                </Typography>
                                            }
                                            secondary={
                                                <>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ display: 'block' }}
                                                    >
                                                        {n.message}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDistanceToNowStrict(new Date(n.createdAt), {
                                                            addSuffix: true,
                                                            locale: fr,
                                                        })}
                                                    </Typography>
                                                </>
                                            }
                                        />
                                        {!allowed && (
                                            <Tooltip title={t('header.noPermissionForNotification')}>
                                                <LockIcon fontSize="small" color="disabled" sx={{ ml: 1 }} />
                                            </Tooltip>
                                        )}
                                    </ListItemButton>
                                    {idx < notifications.length - 1 && <Divider />}
                                </Box>
                            );
                        })}
                    </List>
                )}
                <TablePagination
                    component="div"
                    count={table.data.totalElements}
                    page={table.paginationModel.page}
                    rowsPerPage={table.paginationModel.pageSize}
                    rowsPerPageOptions={[10, 20, 50, 100].includes(table.paginationModel.pageSize)
                        ? [10, 20, 50, 100]
                        : [10, 20, 50, 100, table.paginationModel.pageSize].sort((a, b) => a - b)}
                    onPageChange={(_, page) => table.handlePageChange(page)}
                    onRowsPerPageChange={(e) => table.handlePageSizeChange(parseInt(e.target.value, 10))}
                    labelRowsPerPage="Lignes par page :"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
                />
            </Card>
        </Box>
    );
};

export default NotificationsList;
